import { useQuery } from "@tanstack/react-query";
import { api, readJson } from "../lib/api";
import type { UnitSystem } from "../lib/units";
import { useAuth } from "./useAuth";

export type Site = { id: number; name: string; active: boolean };
type OrgSettings = {
  unit_system?: UnitSystem; sites?: Site[];
  leaderboard_public?: boolean; leaderboard_visible?: boolean; season_cards?: boolean;
};

/** GET /api/org/settings, shared by units and sites. Any failure yields {}
 * (metric, no sites) so neither feature can block a screen from rendering. */
export function useOrgSettingsQuery() {
  return useQuery({
    queryKey: ["org-settings"],
    queryFn: async (): Promise<OrgSettings> => {
      try {
        const res = await api.get("/api/org/settings");
        if (!res.ok) return {};
        return await readJson<OrgSettings>(res);
      } catch {
        return {};
      }
    },
    staleTime: 60_000,
    retry: false,
  });
}

/** Org unit system. Defaults to metric while loading or on any error — never blocks rendering. */
export function useUnits(): UnitSystem {
  const { data } = useOrgSettingsQuery();
  return data?.unit_system === "imperial" ? "imperial" : "metric";
}

/** Whether THIS viewer may see the crew leaderboard (server-computed: owner,
 * managers, or everyone once the owner turns it on). Hidden while loading or
 * on any error — the API refuses workers regardless. */
export function useLeaderboardVisible(): boolean {
  // Keyed by identity (unlike useOrgSettingsQuery) because the answer is
  // per-viewer: a cached "no" from before sign-in must not stick.
  const { player, role, isOwner } = useAuth();
  // Owner (role "admin") and managers always see it — no round trip needed.
  const managerPlus = role === "manager" || role === "admin";
  const { data } = useQuery({
    queryKey: ["org-settings", "leaderboard-visible", player?.id ?? null, role, isOwner],
    enabled: !managerPlus,
    queryFn: async (): Promise<boolean> => {
      try {
        const res = await api.get("/api/org/settings");
        if (!res.ok) return false;
        return (await readJson<OrgSettings>(res)).leaderboard_visible === true;
      } catch {
        return false;
      }
    },
    staleTime: 60_000,
    retry: false,
  });
  return managerPlus || data === true;
}

/** The org's ACTIVE sites. Empty = single-site org, and every site control
 * in the app hides itself, so nothing changes for the common case. */
export function useSites(): Site[] {
  const { data } = useOrgSettingsQuery();
  return (data?.sites ?? []).filter((s) => s.active);
}
